import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import './Sidebar.css';
import { useAuth } from '../../hooks/useAuth';
import {
  createUser as createUserService,
  deleteManagedUser as deleteManagedUserService,
  listUsers as listUsersService,
  updateManagedUser as updateManagedUserService,
  updateProfile as updateProfileService,
} from '../../services/auth';
import Logo from '../../media/images/logo-app.svg';


const Sidebar = () => {
  const { user, logoutUser, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    username: user?.username || '',
    password: '',
    password_confirm: '',
  });
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersSuccess, setUsersSuccess] = useState('');
  const [isUserSaving, setIsUserSaving] = useState(false);
  const [managedUserForm, setManagedUserForm] = useState({
    id: null,
    username: '',
    first_name: '',
    last_name: '',
    password: '',
    is_superuser: false,
  });

  const close = () => setIsOpen(false);

  const navItems = [
    { to: '/', label: 'Dashboard' },
    { to: '/clienti', label: 'Clienti' },
    { to: '/monitorizare', label: 'Monitorizare' },
    { to: '/ruleaza-monitoring', label: 'Ruleaza monitorizare' },
  ];

  const handleLogout = async () => {
    try {
      await logoutUser();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const openProfileModal = () => {
    setProfileForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      username: user?.username || '',
      password: '',
      password_confirm: '',
    });
    setProfileError('');
    setProfileSuccess('');
    setIsProfileModalOpen(true);
  };

  const closeProfileModal = () => {
    setIsProfileModalOpen(false);
    setProfileForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      username: user?.username || '',
      password: '',
      password_confirm: '',
    });
    setProfileError('');
    setProfileSuccess('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setIsSubmitting(true);

    // Validation
    if (profileForm.password && profileForm.password !== profileForm.password_confirm) {
      setProfileError('Parolele nu se potrivesc');
      setIsSubmitting(false);
      return;
    }

    try {
      const data = await updateProfileService({
        user_id: user?.id,
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        username: profileForm.username,
        password: profileForm.password || undefined,
      });

      if (data?.user) {
        updateUser(data.user);
      }

      setProfileSuccess('Profil actualizat cu succes');
      setTimeout(() => {
        closeProfileModal();
      }, 1500);
    } catch (err) {
      setProfileError(err.message || 'Nu am putut actualiza profilul');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetManagedUserForm = () => {
    setManagedUserForm({
      id: null,
      username: '',
      first_name: '',
      last_name: '',
      password: '',
      is_superuser: false,
    });
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const data = await listUsersService(user?.id);
      setUsersList(data?.users || []);
    } catch (err) {
      setUsersError(err.message || 'Nu am putut încărca utilizatorii');
    } finally {
      setUsersLoading(false);
    }
  };

  const openUsersModal = async () => {
    setUsersError('');
    setUsersSuccess('');
    resetManagedUserForm();
    setIsUsersModalOpen(true);
    await loadUsers();
  };

  const closeUsersModal = () => {
    setIsUsersModalOpen(false);
    setUsersError('');
    setUsersSuccess('');
    resetManagedUserForm();
  };

  const startEditManagedUser = (entry) => {
    setUsersError('');
    setUsersSuccess('');
    setManagedUserForm({
      id: entry.id,
      username: entry.username || '',
      first_name: entry.first_name || '',
      last_name: entry.last_name || '',
      password: '',
      is_superuser: !!entry.is_superuser,
    });
  };

  const handleManagedUserSubmit = async (e) => {
    e.preventDefault();
    setUsersError('');
    setUsersSuccess('');

    if (!managedUserForm.username.trim()) {
      setUsersError('Username este obligatoriu');
      return;
    }

    if (!managedUserForm.id && !managedUserForm.password) {
      setUsersError('Parola este obligatorie la crearea unui utilizator');
      return;
    }

    setIsUserSaving(true);
    try {
      const payload = {
        actor_user_id: user?.id,
        username: managedUserForm.username.trim(),
        first_name: managedUserForm.first_name,
        last_name: managedUserForm.last_name,
        password: managedUserForm.password || undefined,
        is_superuser: managedUserForm.is_superuser,
      };

      let response;
      if (managedUserForm.id) {
        response = await updateManagedUserService(managedUserForm.id, payload);
        setUsersSuccess('Utilizator actualizat cu succes');
      } else {
        response = await createUserService(payload);
        setUsersSuccess('Utilizator creat cu succes');
      }

      if (response?.user?.id === user?.id) {
        updateUser(response.user);
      }

      resetManagedUserForm();
      await loadUsers();
    } catch (err) {
      setUsersError(err.message || 'Operațiunea a eșuat');
    } finally {
      setIsUserSaving(false);
    }
  };

  const handleDeleteManagedUser = async (targetUserId) => {
    if (!window.confirm('Sigur vrei să ștergi acest utilizator?')) {
      return;
    }

    setUsersError('');
    setUsersSuccess('');
    try {
      await deleteManagedUserService(targetUserId, user?.id);
      setUsersSuccess('Utilizator șters cu succes');
      await loadUsers();
      if (managedUserForm.id === targetUserId) {
        resetManagedUserForm();
      }
    } catch (err) {
      setUsersError(err.message || 'Nu am putut șterge utilizatorul');
    }
  };

  return (
    <>
      {/* Mobile burger button — rendered outside the sidebar so it's always visible */}
      <button
        type="button"
        className="sidebar-burger"
        aria-label="Open menu"
        onClick={() => setIsOpen(true)}
      >
        <span /><span /><span />
      </button>

      {/* Overlay closes sidebar when tapping outside */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={close} aria-hidden="true" />
      )}

      <aside className={`sidebar${isOpen ? ' sidebar-open' : ''}`}>
        {/* Logo */}
        
        {/* Mobile close button inside sidebar */}
        <button
          type="button"
          className="sidebar-close"
          aria-label="Close menu"
          onClick={close}
        >
          ×
        </button>

        <nav className="sidebar-nav">
        <div className="sidebar-logo">
          <img src={Logo} className="sidebar-logo-img" alt="Logo" />
        </div>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={close}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-user-actions">
          {user?.is_superuser && (
            <button type="button" className="header-logout users-btn" onClick={openUsersModal}>
              Utilizatori
            </button>
          )}
          <button type="button" className="header-logout profil-btn" onClick={openProfileModal}>
            Profil
          </button>
          <button type="button" className="header-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      {/* Profile Edit Modal */}
      {isProfileModalOpen && (
        <div className="profile-modal-overlay" onClick={closeProfileModal}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h2>Editează Profil</h2>
              <button
                type="button"
                className="profile-modal-close"
                onClick={closeProfileModal}
              >
                ×
              </button>
            </div>

            <div className="profile-modal-body">
              {profileError && <div className="profile-error">{profileError}</div>}
              {profileSuccess && <div className="profile-success">{profileSuccess}</div>}

              <form onSubmit={handleProfileSubmit}>
                <div className="profile-form-field">
                  <label htmlFor="first-name">Prenume</label>
                  <input
                    id="first-name"
                    type="text"
                    value={profileForm.first_name}
                    onChange={(e) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        first_name: e.target.value,
                      }))
                    }
                    placeholder="Introdu prenumele"
                  />
                </div>

                <div className="profile-form-field">
                  <label htmlFor="last-name">Nume de familie</label>
                  <input
                    id="last-name"
                    type="text"
                    value={profileForm.last_name}
                    onChange={(e) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        last_name: e.target.value,
                      }))
                    }
                    placeholder="Introdu numele de familie"
                  />
                </div>

                <div className="profile-form-field">
                  <label htmlFor="username">Username</label>
                  <input
                    id="username"
                    type="text"
                    value={profileForm.username}
                    onChange={(e) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        username: e.target.value,
                      }))
                    }
                    placeholder="Introdu username-ul"
                  />
                </div>

                <div className="profile-form-field">
                  <label htmlFor="password">Parolă nouă (opțional)</label>
                  <input
                    id="password"
                    type="password"
                    value={profileForm.password}
                    onChange={(e) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    placeholder="Lasă gol dacă nu vrei să schimbi"
                  />
                </div>

                <div className="profile-form-field">
                  <label htmlFor="password-confirm">Confirmă parola</label>
                  <input
                    id="password-confirm"
                    type="password"
                    value={profileForm.password_confirm}
                    onChange={(e) =>
                      setProfileForm((prev) => ({
                        ...prev,
                        password_confirm: e.target.value,
                      }))
                    }
                    placeholder="Confirmă noua parolă"
                  />
                </div>


                <div className="profile-form-actions">
                  <button
                    type="submit"
                    className="profile-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Se actualizează...' : 'Salvează'}
                  </button>
                  <button
                    type="button"
                    className="profile-btn-secondary"
                    onClick={closeProfileModal}
                    disabled={isSubmitting}
                  >
                    Anulează
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {isUsersModalOpen && (
        <div className="profile-modal-overlay" onClick={closeUsersModal}>
          <div className="profile-modal users-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h2>Gestionare utilizatori</h2>
              <button
                type="button"
                className="profile-modal-close"
                onClick={closeUsersModal}
              >
                ×
              </button>
            </div>

            <div className="profile-modal-body">
              {usersError && <div className="profile-error">{usersError}</div>}
              {usersSuccess && <div className="profile-success">{usersSuccess}</div>}

              <form onSubmit={handleManagedUserSubmit} className="users-form">
                <div className="profile-form-field">
                  <label htmlFor="managed-username">Username</label>
                  <input
                    id="managed-username"
                    type="text"
                    value={managedUserForm.username}
                    onChange={(e) =>
                      setManagedUserForm((prev) => ({
                        ...prev,
                        username: e.target.value,
                      }))
                    }
                    placeholder="Adauga username"
                  />
                </div>

                <div className="profile-form-field">
                  <label htmlFor="managed-first-name">Prenume</label>
                  <input
                    id="managed-first-name"
                    type="text"
                    value={managedUserForm.first_name}
                    onChange={(e) =>
                      setManagedUserForm((prev) => ({
                        ...prev,
                        first_name: e.target.value,
                      }))
                    }
                    placeholder="Introdu prenume"
                  />
                </div>

                <div className="profile-form-field">
                  <label htmlFor="managed-last-name">Nume de familie</label>
                  <input
                    id="managed-last-name"
                    type="text"
                    value={managedUserForm.last_name}
                    onChange={(e) =>
                      setManagedUserForm((prev) => ({
                        ...prev,
                        last_name: e.target.value,
                      }))
                    }
                    placeholder="Introdu nume de familie"
                  />
                </div>


                <div className="profile-form-field">
                  <label htmlFor="managed-password">
                    {managedUserForm.id ? 'Parolă nouă (opțional)' : 'Parolă'}
                  </label>
                  <input
                    id="managed-password"
                    type="password"
                    value={managedUserForm.password}
                    onChange={(e) =>
                      setManagedUserForm((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    placeholder={managedUserForm.id ? 'Lasă gol pentru a păstra parola' : 'Introdu parola'}
                  />
                </div>

                <div className="profile-form-field">
                  <label htmlFor="managed-is-superuser">
                    <input
                      id="managed-is-superuser"
                      type="checkbox"
                      checked={!!managedUserForm.is_superuser}
                      onChange={e => setManagedUserForm(prev => ({ ...prev, is_superuser: e.target.checked }))}
                    />
                    {' '}Este admin?
                  </label>
                </div>

                <div className="profile-form-actions">
                  <button type="submit" className="profile-btn-primary" disabled={isUserSaving}>
                    {isUserSaving
                      ? 'Se salvează...'
                      : managedUserForm.id
                        ? 'Actualizează utilizator'
                        : 'Adaugă utilizator'}
                  </button>
                  {managedUserForm.id && (
                    <button
                      type="button"
                      className="profile-btn-secondary"
                      onClick={resetManagedUserForm}
                      disabled={isUserSaving}
                    >
                      Renunță editare
                    </button>
                  )}
                </div>
              </form>

              <div className="users-list-wrap">
                <h3>Utilizatori existenți</h3>
                {usersLoading ? (
                  <p>Se încarcă utilizatorii...</p>
                ) : (
                  <div className="users-list">
                    {usersList.map((entry) => (
                      <div className="users-list-item" key={entry.id}>
                        <div>
                          <strong>{entry.username}</strong>
                          <p>{`${entry.first_name || ''} ${entry.last_name || ''}`.trim() || 'Fără nume complet'}</p>
                        </div>
                        <div className="users-item-actions">
                          <button
                            type="button"
                            className="profile-btn-secondary users-small-btn"
                            onClick={() => startEditManagedUser(entry)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="profile-btn-secondary users-small-btn users-delete-btn"
                            onClick={() => handleDeleteManagedUser(entry.id)}
                          >
                            Șterge
                          </button>
                        </div>
                      </div>
                    ))}
                    {!usersList.length && <p>Nu există utilizatori.</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
